import {SegmentedControl} from '@primer/react'
import {type CommentsFilterState, type AuthorFilterItem, CommentsFilter} from './CommentsFilter'
import {useCommentReplyActions, useCommentsPanelViewMode} from '../../../stores/layout-selectors'
import {ViewMode, type CommentsPanelViewMode} from '../../../stores/slices/comment-view-mode'

export type ViewMode = CommentsPanelViewMode

export function isReviewCommentsViewMode(viewMode: ViewMode): boolean {
  return viewMode === ViewMode.ReviewComments || viewMode === ViewMode.ThreadPreviews
}

interface CommentPanelHeaderContentProps {
  threadPreviewsAuthorList: AuthorFilterItem[]
  issueCommentsAuthorList: AuthorFilterItem[]
  threadPreviewFilterState: CommentsFilterState
  setThreadPreviewFilterState: (state: CommentsFilterState) => void
  issueCommentsFilterState: CommentsFilterState
  setIssueCommentsFilterState: (state: CommentsFilterState) => void
  iconOnly?: boolean
}

/**
 * Renders header controls for the comments side panel.
 *
 * Review and conversation each keep their own filter state.
 */
export function CommentPanelHeaderContent({
  threadPreviewsAuthorList,
  issueCommentsAuthorList,
  threadPreviewFilterState,
  setThreadPreviewFilterState,
  issueCommentsFilterState,
  setIssueCommentsFilterState,
  iconOnly,
}: CommentPanelHeaderContentProps) {
  const viewMode = useCommentsPanelViewMode()
  const {setCommentsPanelViewMode} = useCommentReplyActions()
  const isReviewCommentsMode = isReviewCommentsViewMode(viewMode)

  return (
    <div className="d-flex flex-row flex-items-center gap-2 py-2">
      <SegmentedControl
        aria-label="Comment view mode"
        onChange={(value: number) => {
          const nextViewMode = value === 0 ? ViewMode.ThreadPreviews : ViewMode.IssueComments
          setCommentsPanelViewMode(nextViewMode)
        }}
      >
        <SegmentedControl.Button selected={isReviewCommentsMode}>Review</SegmentedControl.Button>
        <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>Conversation</SegmentedControl.Button>
      </SegmentedControl>
      {isReviewCommentsMode && (
        <CommentsFilter
          authorList={threadPreviewsAuthorList}
          className="width-full"
          filterState={threadPreviewFilterState}
          onFilterStateChange={setThreadPreviewFilterState}
          hideReviewThreadFilters={false}
          iconOnly={iconOnly}
        />
      )}
      {viewMode === ViewMode.IssueComments && (
        <CommentsFilter
          authorList={issueCommentsAuthorList}
          className="width-full"
          filterState={issueCommentsFilterState}
          onFilterStateChange={setIssueCommentsFilterState}
          hideReviewThreadFilters
          iconOnly={iconOnly}
        />
      )}
    </div>
  )
}
